/**
 * 深色商务模板 - 列表页
 * 
 * Banner设计：全宽几何线条风格 + 居中大标题带装饰下划线，与绿色模板完全不同
 */

import { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import {
  getSiteCategories,
  getListings,
  getNavCategories,
  type FrontendCategory,
  type ListingItem,
} from "@/utils/api";
import { useSite } from "@/context/SiteContext";

const SORT_OPTIONS = [
  { key: "latest", label: "最新", icon: "ri-time-line" },
  { key: "popular", label: "最热", icon: "ri-fire-line" },
];

export default function DarkCategoryPage() {
  const { slug } = useParams<{ slug?: string }>();
  const { siteId, loading: siteLoading } = useSite();

  const [categories, setCategories] = useState<FrontendCategory[]>([]);
  const [navCategories, setNavCategories] = useState<Array<{ id: string; name: string; slug: string; icon: string }>>([]);
  const [listings, setListings] = useState<ListingItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [activeCategoryId, setActiveCategoryId] = useState<string>("");
  const [sortBy, setSortBy] = useState("latest");
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 12;

  useEffect(() => {
    if (siteLoading || !siteId) return;
    Promise.all([
      getSiteCategories().then(setCategories).catch(() => {}),
      getNavCategories().then(setNavCategories).catch(() => {}),
    ]);
  }, [siteId, siteLoading]);

  useEffect(() => {
    if (slug && categories.length > 0) {
      const found = categories.find((c) => c.slug === slug || c.id === slug);
      if (found) setActiveCategoryId(found.id);
    }
  }, [slug, categories]);

  useEffect(() => {
    if (siteLoading || !siteId) return;
    setLoading(true);
    const params: Record<string, any> = { page: currentPage, page_size: PAGE_SIZE };
    if (activeCategoryId) params.category_id = activeCategoryId;
    getListings(params)
      .then((data) => {
        setListings(data.list || []);
        setTotal(data.total || 0);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [siteId, siteLoading, activeCategoryId, currentPage]);

  const currentCategoryName = useMemo(() => {
    if (activeCategoryId) {
      const cat = categories.find((c) => c.id === activeCategoryId);
      return cat?.name || "全部分类";
    }
    return "全部分类";
  }, [activeCategoryId, categories]);

  const sortedListings = useMemo(() => {
    const list = [...listings];
    if (sortBy === "popular") list.sort((a, b) => (b.view_count || 0) - (a.view_count || 0));
    return list;
  }, [listings, sortBy]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="min-h-screen bg-gray-950">
      <Navbar />

      {/* ── 页面 Banner：全宽几何线条风格 + 居中大标题 ── */}
      <section className="relative bg-gray-950 py-16 md:py-24 overflow-hidden">
        {/* 几何线条装饰网格 */}
        <div className="absolute inset-0 pointer-events-none select-none overflow-hidden">
          {/* 大十字线 */}
          <div className="absolute top-0 left-1/2 w-px h-full bg-gradient-to-b from-transparent via-amber-500/10 to-transparent" />
          <div className="absolute top-1/2 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/10 to-transparent" />
          {/* 对角线 */}
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gray-800 to-transparent rotate-12 origin-center scale-150 opacity-30" />
          <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gray-800 to-transparent -rotate-12 origin-center scale-150 opacity-30" />
          {/* 角框装饰 */}
          <div className="absolute top-8 left-8 w-12 h-12 border-t border-l border-amber-500/20 opacity-40" />
          <div className="absolute top-8 right-8 w-12 h-12 border-t border-r border-amber-500/20 opacity-40" />
          <div className="absolute bottom-8 left-8 w-12 h-12 border-b border-l border-amber-500/20 opacity-40" />
          <div className="absolute bottom-8 right-8 w-12 h-12 border-b border-r border-amber-500/20 opacity-40" />
          {/* 光晕 */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-amber-500/3 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 text-center">
          {/* 顶部面包屑 - 行内标签风格 */}
          <div className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-gray-900/80 border border-gray-800 rounded-full text-sm backdrop-blur-sm mb-8">
            <Link to="/" className="text-gray-500 hover:text-amber-400 transition-colors">首页</Link>
            <span className="text-gray-700">/</span>
            <span className="text-amber-400 font-medium">{currentCategoryName}</span>
          </div>

          {/* 大标题 + 装饰线 */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 tracking-tight">
            {currentCategoryName}
          </h1>

          {/* 金色装饰下划线 */}
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-8 h-px bg-gradient-to-r from-transparent to-amber-500/60" />
            <div className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
            <div className="w-16 h-px bg-amber-500/60" />
            <div className="w-2 w-2 bg-amber-400 rounded-full" />
            <div className="w-8 h-px bg-gradient-to-l from-transparent to-amber-500/60" />
          </div>

          <p className="text-gray-500 text-base max-w-md mx-auto">
            专业服务 · 品质保障 · 即刻发现
          </p>

          {/* 底部渐变过渡线 */}
          <div className="mt-10 w-32 h-px mx-auto bg-gradient-to-r from-transparent via-gray-700 to-transparent" />
        </div>
      </section>

      {/* ── 分类筛选标签条 ── */}
      <div className="sticky top-[60px] z-20 bg-gray-950/80 backdrop-blur-md border-b border-gray-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
            <button
              onClick={() => { setActiveCategoryId(""); setCurrentPage(1); }}
              className={`shrink-0 px-4 py-2 rounded-xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                !activeCategoryId
                  ? "bg-amber-500 text-gray-900 shadow-lg shadow-amber-500/20"
                  : "bg-gray-800 text-gray-400 hover:bg-gray-750 hover:text-gray-200 border border-gray-700"
              }`}
            >
              全部
            </button>
            {(navCategories.length > 0 ? navCategories : categories).map(cat => (
              <button
                key={cat.id}
                onClick={() => { setActiveCategoryId(cat.id === activeCategoryId ? "" : cat.id); setCurrentPage(1); }}
                className={`shrink-0 px-4 py-2 rounded-xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  activeCategoryId === cat.id
                    ? "bg-amber-500 text-gray-900 shadow-lg shadow-amber-500/20"
                    : "bg-gray-800 text-gray-400 hover:bg-gray-750 hover:text-gray-200 border border-gray-700"
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {/* ── 工具栏 ── */}
        <div className="flex items-center justify-between mb-6">
          <span className="text-sm text-gray-600">
            <i className="ri-stack-line mr-1.5 text-amber-500/50" />
            探索服务列表
          </span>
          <div className="flex items-center gap-1 bg-gray-900 border border-gray-700 rounded-xl p-0.5">
            {SORT_OPTIONS.map(opt => (
              <button
                key={opt.key}
                onClick={() => setSortBy(opt.key)}
                className={`flex items-center gap-1 px-3.5 py-1.5 text-xs font-medium rounded-lg transition-all cursor-pointer ${
                  sortBy === opt.key
                    ? "bg-amber-500 text-gray-900 shadow-sm"
                    : "text-gray-400 hover:text-gray-200"
                }`}
              >
                <i className={opt.icon} />{opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── 暗黑卡片网格 ── */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map(i => (
              <div key={i} className="animate-pulse bg-gray-900 rounded-2xl overflow-hidden border border-gray-800">
                <div className="w-full h-44 bg-gray-800" />
                <div className="p-4 space-y-2.5">
                  <div className="w-3/4 h-4 bg-gray-800 rounded-lg" />
                  <div className="w-full h-3 bg-gray-800 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : sortedListings.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sortedListings.map((listing, index) => (
              <Link
                key={listing.id}
                to={`/listing/${listing.id}`}
                className="group relative bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden hover:border-amber-500/30 hover:shadow-xl hover:shadow-amber-500/5 transition-all duration-500"
              >
                {/* 图片 */}
                <div className="relative w-full h-44 bg-gradient-to-br from-gray-800 to-gray-900 overflow-hidden">
                  {listing.images?.[0] ? (
                    <img src={listing.images[0]} alt={listing.title} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-700" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <i className="ri-image-line text-3xl text-gray-700" />
                    </div>
                  )}
                  {/* 顶部光条 */}
                  <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-amber-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                  {listing.is_pinned && (
                    <span className="absolute top-3 left-3 px-2 py-0.5 bg-amber-500 text-gray-900 text-xs font-bold rounded-lg flex items-center gap-1">
                      <i className="ri-pushpin-line" />置顶
                    </span>
                  )}
                  <span className="absolute bottom-3 right-3 px-2 py-0.5 bg-black/60 backdrop-blur-sm text-gray-300 text-xs rounded-lg">
                    {listing.category?.name}
                  </span>
                </div>

                {/* 内容 */}
                <div className="p-4">
                  <h3 className="font-bold text-gray-200 text-sm line-clamp-2 group-hover:text-amber-400 transition-colors">
                    {listing.title}
                  </h3>
                  <p className="text-gray-500 text-xs mt-2 line-clamp-2 leading-relaxed">{listing.content}</p>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-800 text-xs text-gray-600">
                    <span className="flex items-center gap-1"><i className="ri-user-line" />{listing.contact_name || '匿名'}</span>
                    <div className="flex items-center gap-3">
                      <span>{formatShortDate(listing.created_at)}</span>
                      <span className="flex items-center gap-1"><i className="ri-eye-line" />{listing.view_count}</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 bg-gray-900 rounded-2xl border border-dashed border-gray-800">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-gray-800">
              <i className="ri-search-line text-2xl text-gray-600" />
            </div>
            <p className="text-gray-500 font-medium">暂无相关服务</p>
            <p className="text-gray-600 text-sm mt-1">试试其他分类或关键词</p>
          </div>
        )}

        {/* ── 分页 ── */}
        {!loading && totalPages > 1 && (
          <div className="mt-10 flex items-center justify-center gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="w-10 h-10 flex items-center justify-center rounded-xl bg-gray-900 border border-gray-700 text-gray-500 hover:border-amber-500/30 hover:text-amber-400 disabled:opacity-30 transition-all cursor-pointer"
            >
              <i className="ri-arrow-left-s-line" />
            </button>

            {generatePagination(currentPage, totalPages).map((p, i) => (
              <button
                key={i}
                onClick={() => typeof p === 'number' ? setCurrentPage(p) : null}
                disabled={typeof p !== 'number'}
                className={`w-10 h-10 flex items-center justify-center rounded-xl text-sm font-semibold transition-all cursor-pointer ${
                  currentPage === p
                    ? "bg-amber-500 text-gray-900 shadow-lg shadow-amber-500/20"
                    : typeof p === 'number'
                    ? "bg-gray-900 border border-gray-700 text-gray-400 hover:border-amber-500/30 hover:text-amber-400"
                    : "text-gray-600"
                }`}
              >
                {typeof p === 'number' ? p : '···'}
              </button>
            ))}

            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="w-10 h-10 flex items-center justify-center rounded-xl bg-gray-900 border border-gray-700 text-gray-500 hover:border-amber-500/30 hover:text-amber-400 disabled:opacity-30 transition-all cursor-pointer"
            >
              <i className="ri-arrow-right-s-line" />
            </button>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}

function formatShortDate(dateStr: string): string {
  const d = new Date(dateStr);
  return `${d.getMonth() + 1}月${d.getDate()}日`;
}

function generatePagination(current: number, total: number): (number | string)[] {
  const pages: (number | string)[] = [];
  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
  } else {
    pages.push(1);
    if (current > 3) pages.push('...');
    for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i);
    if (current < total - 2) pages.push('...');
    pages.push(total);
  }
  return pages;
}
